import React, { useState, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import _ from 'underscore';
import {
  mergeClasses,
  Tab,
  TabList,
  Text,
  SelectTabEvent,
  SelectTabData,
} from '@fluentui/react-components';
import { ILogger } from '@bingads-webui/ads-copilot-shared-types';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Entity } from './entity';
import { useEntitySelectionData } from './use-entity-selection-data';
import { EntitySelectionMenuShimmer } from './entity-selection-menu-shimmer';
import { getEntitiesFilteredBySearchPhrase, TabNames, EntitiesErrorLabels } from './entity-selection-utils';
import { EntitySelectionErrorView } from './entity-selection-error-view';
import { EntitySelectionNoResultsView } from './entity-selection-no-results-view';
import { EntitySearchBox } from './entity-search-box';
import { useEntitySelectionMenuStyles } from './entity-selection-styles';
import { IAttachmentItem } from './types';

interface IEntitySelectionMenu {
  accountId?: string;
  customerId?: string;
  ccToken?: string;
  searchPhrase?: string;
  onClickEntity: (entity: IAttachmentItem) => void;
  width?: number;
  logger?: ILogger;
  perfMarker?: any;
  entities?: ReturnType<typeof useEntitySelectionData>;
  targetTab?: string;
  onMenuRendered?: () => void;
}

export const EntitySelectionMenu = (props: IEntitySelectionMenu) => {
  const {
    onClickEntity,
    searchPhrase: searchPhraseInChatInput,
    width,
    logger,
    perfMarker,
    entities,
    targetTab,
    onMenuRendered,
  } = props;

  const i18n = useContext(I18nContext) as I18nModel;
  const styles = useEntitySelectionMenuStyles();
  type ITabNames = keyof typeof TabNames;
  const [activeTab, setActiveTab] = useState<ITabNames>('all');
  const [searchBoxValue, setSearchBoxValue] = useState<string | undefined>(undefined);
  const [searchPhrase, setSearchPhrase] = useState<string | undefined>(undefined);
  const [focusedEntityIndex, setFocusedEntityIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const entitiesFilteredBySearch = useMemo(() => getEntitiesFilteredBySearchPhrase(entities, searchPhrase), [entities, searchPhrase]);

  const handleEntityKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentEntities = entitiesFilteredBySearch[activeTab];
    if (currentEntities.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = focusedEntityIndex === currentEntities.length - 1 ? 0 : focusedEntityIndex + 1;
      setFocusedEntityIndex(nextIndex);
      const buttons = contentRef.current?.querySelectorAll('.entity-item');
      (buttons?.[nextIndex] as HTMLElement)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = focusedEntityIndex === 0 ? currentEntities.length - 1 : focusedEntityIndex - 1;
      setFocusedEntityIndex(prevIndex);
      const buttons = contentRef.current?.querySelectorAll('.entity-item');
      (buttons?.[prevIndex] as HTMLElement)?.focus();
    }
  }, [activeTab, entitiesFilteredBySearch, focusedEntityIndex]);

  const onTabSelect = useCallback((event: SelectTabEvent, data: SelectTabData) => {
    const newValue = data.value as ITabNames;
    setActiveTab(newValue);
    setFocusedEntityIndex(0);
  }, []);

  // Reset focus when entities change
  useEffect(() => {
    setFocusedEntityIndex(0);
  }, [activeTab, searchPhrase]);

  useEffect(() => {
    perfMarker?.willInit();
  }, [perfMarker]);

  useEffect(() => {
    logger?.trace(`[EntitySelection] Entity selection menu active tab: ${activeTab}`);
  }, [activeTab, logger]);

  const onSearchBoxChange = useCallback((_e: any, data: { value: string }) => {
    const newSearchPhrase = data.value.trim();
    setSearchBoxValue(newSearchPhrase);
    logger?.trace(`[EntitySelection] Search box in entity selection menu update: ${newSearchPhrase}`);
  }, [logger]);

  useEffect(() => {
    if (searchBoxValue && searchBoxValue.length > 0) {
      setSearchPhrase(searchBoxValue);
    } else {
      setSearchPhrase(searchPhraseInChatInput);
    }
  }, [searchBoxValue, searchPhraseInChatInput]);

  useEffect(() => {
    if (targetTab) {
      const tabName = _.find(Object.keys(TabNames), (name: string) => name.toLowerCase() === targetTab.toLowerCase());
      if (tabName) {
        setActiveTab(tabName as ITabNames);
      }
    }
    onMenuRendered?.();
  }, [onMenuRendered, targetTab]);

  const renderTabItems = useCallback(() => {
    if (entitiesFilteredBySearch.isLoading) {
      logger?.trace('[EntitySelection] Render enity selection menu loading view.');
      return <EntitySelectionMenuShimmer />;
    }

    perfMarker?.done();

    if (entitiesFilteredBySearch.error) {
      logger?.trace(`[EntitySelection] Render enity selection menu error view. Error: ${JSON.stringify(entitiesFilteredBySearch.error)})`);
      return (
        <EntitySelectionErrorView
          i18n={i18n}
          onRetry={entities?.refetch}
        />
      );
    }

    if (entities && entities[activeTab].length === 0) {
      logger?.trace('[EntitySelection] Render enity selection menu empty results view. This account has no entities.');
      return (
        <div className={styles.content}>
          <EntitySelectionNoResultsView
            i18n={i18n}
            entityType={EntitiesErrorLabels[activeTab]}
            hasNoEntityTypeInAccount
          />
        </div>
      );
    }

    if (entitiesFilteredBySearch[activeTab].length === 0 && searchPhrase) {
      logger?.trace('[EntitySelection] Render enity selection menu empty results view. No results after filtering.');
      return (
        <div className={styles.content}>
          <EntitySelectionNoResultsView
            i18n={i18n}
            hasNoEntityTypeInAccount={false}
          />
        </div>
      );
    }

    logger?.trace(`[EntitySelection] Render entity selection menu content. activeTab: ${activeTab}, showed entity length: ${entitiesFilteredBySearch[activeTab].length}`);
    return (
      <div
        className={mergeClasses(styles.content, 'entity-selection-menu-content')}
        ref={contentRef}
        onKeyDown={handleEntityKeyDown}
        tabIndex={-1}
        role="listbox"
        aria-label={i18n.getString(_TL_('Entity list'))}
      >
        {
          _.map(entitiesFilteredBySearch[activeTab], (entity: IAttachmentItem) => (
            <Entity
              {...entity}
              onClick={onClickEntity}
              key={entity.id}
            />
          ))
        }
      </div>
    );
  }, [
    activeTab, entities, entitiesFilteredBySearch, handleEntityKeyDown,
    i18n, logger, onClickEntity, perfMarker, searchPhrase, styles.content,
  ]);

  return (
    <div className={mergeClasses(styles.entitySelectionMenuContainer, 'entity-selection-menu')} style={{ width: width ? `${width - 8}px` : '100%' }}>
      <div className={styles.entityHeader}>
        <div className={styles.entityTabs}>
          <TabList
            selectedValue={activeTab}
            onTabSelect={onTabSelect}
            className={styles.tabList}
          >
            {Object.keys(TabNames).map((key) => {
            const typedKey = key as ITabNames;
            return (
              <Tab
                key={typedKey}
                value={typedKey}
              >
                <Text> {i18n.getString(TabNames[typedKey])} </Text>
              </Tab>
            );
          })}
          </TabList>
        </div>
        <div className={styles.searchContainer}>
          <EntitySearchBox
            i18n={i18n}
            activeTab={activeTab}
            disabled={entitiesFilteredBySearch.isLoading || !_.isEmpty(entitiesFilteredBySearch.error)}
            onChange={onSearchBoxChange}
            value={searchPhrase}
          />
        </div>
      </div>
      { renderTabItems() }
    </div>
  );
};
